import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserCog, ShieldCheck, FileText, Mail, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { TrackingLayout, RequestError } from "@/components/tracking/TrackingLayout";

const SUPPORT_EMAIL = "mentoriafabioborges@gmail.com";

export default function Account() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const remove = async () => {
    setDeleting(true); setError("");
    try {
      await base44.auth.deleteAccount(password);
      window.location.href = "/login";
    } catch (failure) { setError(failure.message); setDeleting(false); }
  };
  const links = [
    { to: "/privacy", label: t("account.privacy"), icon: ShieldCheck, testId: "account-privacy" },
    { to: "/terms", label: t("account.terms"), icon: FileText, testId: "account-terms" },
  ];
  return <TrackingLayout title={t("account.title")} icon={UserCog} testId="account">
    <RequestError error={error} testId="account-error" />
    <div className="bg-card border border-border rounded-xl p-5 space-y-1" data-testid="account-identity">
      <p className="font-semibold">{user?.full_name}</p>
      <p className="text-sm text-muted-foreground break-all">{user?.email}</p>
    </div>

    {links.map(({ to, label, icon: Icon, testId }) => <Link key={to} to={to} data-testid={testId}
      className="bg-card border border-border rounded-xl p-5 flex items-center gap-4 hover:border-primary/50 transition-colors">
      <Icon className="w-5 h-5 text-primary shrink-0" /><span className="flex-1 font-medium">{label}</span><span className="text-muted-foreground">›</span>
    </Link>)}

    <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="account-support"
      className="bg-card border border-border rounded-xl p-5 flex items-center gap-4 hover:border-primary/50 transition-colors">
      <Mail className="w-5 h-5 text-primary shrink-0" /><span className="flex-1 font-medium break-all">{t("common.supportEmail")}</span>
    </a>

    <div className="bg-red-950/20 border border-red-800/40 rounded-xl p-5 space-y-3" data-testid="account-danger-zone">
      <h2 className="text-base md:text-lg font-semibold text-red-300">{t("account.deleteTitle")}</h2>
      <p className="text-sm text-red-200/80">{t("account.deleteBody")}</p>
      {!confirming ? <Button onClick={() => setConfirming(true)} data-testid="account-delete-start" variant="outline" className="w-full h-12 text-red-400">
        <Trash2 className="w-4 h-4 mr-2" />{t("account.deleteAction")}
      </Button> : <div className="space-y-3">
        <label htmlFor="account-delete-password" className="text-sm text-red-200/90 block">{t("account.deleteConfirmLabel")}</label>
        <Input id="account-delete-password" data-testid="account-delete-password" type="password" autoComplete="current-password"
          value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t("common.password")} />
        <div className="flex gap-3">
          <Button onClick={() => { setConfirming(false); setPassword(""); setError(""); }} disabled={deleting} data-testid="account-delete-cancel" variant="outline" className="flex-1">{t("common.cancel")}</Button>
          <Button onClick={remove} disabled={deleting || password.length < 6} data-testid="account-delete-confirm" className="flex-1 bg-red-700 hover:bg-red-600 text-white">
            {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("account.deleteConfirm")}
          </Button>
        </div>
      </div>}
    </div>
  </TrackingLayout>;
}
